import React, { useState } from 'react';
import { Mail, MapPin, Phone, Printer, Clock, Loader2, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import PageHead from '@/components/PageHead';
import { toast } from 'sonner';
import { submitContact } from '@/lib/api';

const initial = { name: '', email: '', subject: '', message: '' };

export default function ContactPage() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Nom requis';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email invalide';
    if (!form.message.trim()) e.message = 'Message requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitContact(form);
      setSuccess(true);
      toast.success('Message envoyé — nous vous répondrons rapidement.');
      setForm(initial);
    } catch (e) {
      toast.error('Erreur lors de l’envoi. Réessayez ou contactez-nous par téléphone.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHead title="Contact" description="Contactez ALOFI SA : 92 Allée de la Picholine, 30320 Marguerittes. Tél 05 37 07 81 09 — contact@alofisa.eu." />
      <section className="hero-bg noise-overlay relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px] bg-white/80">Contact</Badge>
          <h1 className="mt-3 font-heading text-3xl sm:text-5xl font-semibold tracking-tight">Parlons de votre approvisionnement</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Nous sommes votre partenaire de confiance pour un approvisionnement fiable et rentable. Contactez-nous, nous revenons vers vous sous 24-48h.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-5 gap-8">
        <Card className="lg:col-span-2 rounded-2xl border-border p-7 h-fit">
          <h2 className="font-heading text-xl font-semibold">Coordonnées</h2>
          <Separator className="my-4" />
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />
              <div>
                <div className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Adresse</div>
                <div data-testid="contact-address">92 Allée de la Picholine, Quartier Pérouse Ouest, 30320 Marguerittes, France</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />
              <div>
                <div className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Téléphone</div>
                <a href="tel:0537078109" className="hover:text-[hsl(186_55%_38%)]" data-testid="contact-phone">05 37 07 81 09</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Printer className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />
              <div>
                <div className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Fax</div>
                <div>05 37 07 99 90</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />
              <div>
                <div className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Email</div>
                <a href="mailto:contact@alofisa.eu" className="hover:text-[hsl(186_55%_38%)]" data-testid="contact-email">contact@alofisa.eu</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />
              <div>
                <div className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Horaires</div>
                <div>Lundi – Vendredi : 9h – 17h</div>
                <div className="text-muted-foreground text-xs">Samedi & Dimanche : Fermé</div>
              </div>
            </li>
          </ul>
          <div className="mt-6 rounded-xl overflow-hidden border border-border">
            <iframe
              title="Localisation Alofisa"
              src="https://www.google.com/maps?q=92+Allee+de+la+Picholine,+30320+Marguerittes,+France&output=embed"
              className="w-full h-56 border-0"
              loading="lazy"
            />
          </div>
        </Card>

        <Card className="lg:col-span-3 rounded-2xl border-border p-7">
          <h2 className="font-heading text-xl font-semibold">Envoyez-nous un message</h2>
          <Separator className="my-4" />
          {success ? (
            <div className="py-10 text-center">
              <div className="w-14 h-14 rounded-full bg-[hsl(186_45%_92%)] flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-[hsl(186_55%_38%)]" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold">Message envoyé</h3>
              <p className="text-sm text-muted-foreground mt-1">Une confirmation a été envoyée à votre adresse email.</p>
              <Button onClick={() => setSuccess(false)} className="rounded-xl mt-5" data-testid="contact-form-new-message">Nouveau message</Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
              <div>
                <Label htmlFor="cname">Nom *</Label>
                <Input id="cname" name="name" value={form.name} onChange={update('name')} className="mt-1" data-testid="contact-name-input" />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="cemail">Email *</Label>
                <Input id="cemail" name="email" type="email" value={form.email} onChange={update('email')} className="mt-1" data-testid="contact-email-input" />
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="csubject">Sujet</Label>
                <Input id="csubject" name="subject" value={form.subject} onChange={update('subject')} className="mt-1" data-testid="contact-subject-input" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cmsg">Message *</Label>
                <Textarea id="cmsg" name="message" rows={6} value={form.message} onChange={update('message')} className="mt-1" data-testid="contact-message-input" />
                {errors.message && <p className="text-xs text-destructive mt-1">{errors.message}</p>}
              </div>
              <div className="sm:col-span-2 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">En envoyant ce formulaire vous acceptez d’être recontacté par ALOFI SA.</p>
                <Button type="submit" disabled={submitting} className="rounded-xl" data-testid="contact-form-submit-button">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Envoyer
                </Button>
              </div>
            </form>
          )}
        </Card>
      </section>
    </div>
  );
}
